import type { IGlobalConfigurationUpdate } from '../redux/configuration/types';
import { sanitizeContentScrubPatch } from './contentScrubUtils';

/** Server-owned / read-only keys — never include in PATCH body. */
const READ_ONLY_CONFIG_KEYS = new Set([
    '_id',
    'ring',
    'client',
    'country',
    'created_by',
    'permissions',
    'filters',
    'currency_options',
    'default_currency',
    'sales_fees',
    'location',
    '__v',
]);

export interface IGlobalConfigurationPatchBody extends IGlobalConfigurationUpdate {
    apply_to_all?: boolean;
    override_all?: boolean;
}

export interface IGlobalConfigurationSaveStep {
    config: IGlobalConfigurationPatchBody;
    applyToAll?: boolean;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Deep diff — returns only changed leaves / nested partials. */
export function buildGlobalConfigurationPatch(
    current: IGlobalConfigurationUpdate,
    baseline: IGlobalConfigurationUpdate,
): IGlobalConfigurationPatchBody {
    const patch: IGlobalConfigurationPatchBody = {};

    const keys = new Set([...Object.keys(current ?? {}), ...Object.keys(baseline ?? {})]);

    keys.forEach(key => {
        if (READ_ONLY_CONFIG_KEYS.has(key)) return;

        const nextValue = (current as Record<string, unknown>)[key];
        const prevValue = (baseline as Record<string, unknown>)[key];

        if (Array.isArray(nextValue) || Array.isArray(prevValue)) {
            if (JSON.stringify(nextValue) !== JSON.stringify(prevValue)) {
                (patch as Record<string, unknown>)[key] = nextValue;
            }
            return;
        }

        if (isPlainObject(nextValue) && isPlainObject(prevValue)) {
            const nested = buildGlobalConfigurationPatch(
                nextValue as IGlobalConfigurationUpdate,
                prevValue as IGlobalConfigurationUpdate,
            );
            if (Object.keys(nested).length > 0) {
                (patch as Record<string, unknown>)[key] = nested;
            }
            return;
        }

        if (nextValue !== prevValue) {
            (patch as Record<string, unknown>)[key] = nextValue;
        }
    });

    return sanitizeContentScrubPatch(patch);
}

/**
 * When apply-to-all is enabled, every changed field is sent in one bulk PATCH.
 * Otherwise only changed fields are sent for the selected country.
 */
export function buildGlobalConfigurationSaveSteps(
    patch: IGlobalConfigurationPatchBody,
    applyToAllCountries: boolean,
): IGlobalConfigurationSaveStep[] {
    if (Object.keys(patch).length === 0) return [];

    if (applyToAllCountries) {
        return [
            {
                config: sanitizeContentScrubPatch({ apply_to_all: true, ...patch }),
                applyToAll: true,
            },
        ];
    }

    return [{ config: patch }];
}
